import React from "react";
import { Box, Typography } from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { acme, acmeMarketingHeadlineSx, acmeMarketingBodyLeadSx, acmeMarketingEyebrowSx } from "../../theme/acmeTheme";
import GlowyWavesBackground from "../Home/GlowyWavesBackground";
import { aboutStats } from "./aboutUsData";

const fadeUp = {
  "@keyframes fadeUp": {
    from: { opacity: 0, transform: "translateY(20px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
};

const gradientTextSx = {
  background: `linear-gradient(90deg, ${acme.colors.brand} 0%, #16a34a 45%, ${acme.colors.foreground} 100%)`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  display: "inline",
};

export default function AboutHero() {
  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        width: "100%",
        minHeight: { xs: "calc(100svh - 64px)", md: "calc(100svh - 72px)" },
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        isolation: "isolate",
        ...fadeUp,
      }}
    >
      <GlowyWavesBackground />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 6, md: 8 },
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            maxWidth: 760,
            mx: "auto",
            mb: { xs: 5, md: 7 },
            animation: "fadeUp 0.6s ease-out forwards",
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              mb: 2.5,
              px: 1.75,
              py: 0.75,
              borderRadius: acme.radius.full,
              border: `1px solid ${acme.colors.border}`,
              backgroundColor: "rgba(255,255,255,0.72)",
              backdropFilter: "blur(8px)",
            }}
          >
            <GroupsOutlinedIcon sx={{ fontSize: 16, color: acme.colors.brand }} />
            <Typography
              sx={{
                ...acmeMarketingEyebrowSx,
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                color: acme.colors.mutedForegroundSolid,
              }}
            >
              About Mailsball
            </Typography>
          </Box>

          <Typography
            component="h1"
            sx={{
              ...acmeMarketingHeadlineSx,
              color: acme.colors.foreground,
              fontSize: { xs: "2.35rem", sm: "3rem", md: "3.5rem" },
              mb: 2,
            }}
          >
            Revolutionizing{" "}
            <Box component="span" sx={gradientTextSx}>
              email marketing
            </Box>
          </Typography>

          <Typography
            sx={{
              ...acmeMarketingBodyLeadSx,
              fontSize: { xs: "1rem", sm: "1.125rem" },
              color: acme.colors.mutedForegroundSolid,
            }}
          >
            We&apos;re on a mission to make email marketing more powerful, intuitive, and
            successful for businesses of all sizes. Join over 1,000 companies who trust
            Mailsball to grow through smart email & SMS marketing.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
            gap: 2,
            animation: "fadeUp 0.8s ease-out 0.12s both",
          }}
        >
          {aboutStats.map((stat) => (
            <Box
              key={stat.label}
              sx={{
                textAlign: "center",
                p: { xs: 2.5, md: 3 },
                borderRadius: acme.radius.lg,
                border: `1px solid ${acme.colors.border}`,
                backgroundColor: "rgba(255,255,255,0.78)",
                backdropFilter: "blur(10px)",
                transition: "border-color 0.2s ease",
                "&:hover": {
                  borderColor: `${acme.colors.brand}44`,
                },
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: acme.radius.md,
                  backgroundColor: acme.colors.brandMuted,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 1.5,
                  "& .MuiSvgIcon-root": { fontSize: 22, color: acme.colors.brand },
                }}
              >
                {stat.icon}
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.35rem", md: "1.65rem" },
                  color: acme.colors.foreground,
                  letterSpacing: "-0.025em",
                  mb: 0.25,
                }}
              >
                {stat.number}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  color: acme.colors.mutedForegroundSolid,
                  fontWeight: 500,
                }}
              >
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
